import { ActionTree, GetterTree, Module, MutationTree } from 'vuex'
import { RootState } from '@/store'
import { RequestParams } from '@/modules/common/apiConfig'
import axios, { AxiosRequestConfig } from 'axios'
import { ItemAttributes } from '@/modules/items/types/itemTypes'
import {
	BillingsAttributes,
	CancelledPlanAttributes,
	PaymentAttributes,
	PaymentHistoryAttributes,
	StripeCustomerAttributes,
} from '@/modules/payments/types/paymentTypes'
import { errorSummary, RemoteLog } from '@/modules/common/utils/loggingUtils'

export type State = {
	items: ItemAttributes[]
	cancelledPlans: CancelledPlanAttributes[]
	billingsLoading: boolean
	payments: PaymentAttributes[]
	paymentsLoading: boolean
	paymentsFailed: boolean
}

export const state = (): State => ({
	items: [],
	cancelledPlans: [],
	billingsLoading: false,
	payments: [],
	paymentsLoading: false,
	paymentsFailed: false,
})

const mutations: MutationTree<State> = {
	setBillingData(state: State, data: BillingsAttributes) {
		const { items, cancelled_plans } = data
		state.items = items
		state.cancelledPlans = cancelled_plans ?? []
	},
	setItemsLoading(state: State, loading: boolean) {
		state.billingsLoading = loading
	},
	setPayments(state: State, payments: PaymentAttributes[]) {
		state.payments = payments
	},
	setPaymentsLoading(state: State, loading: boolean) {
		state.paymentsLoading = loading
	},
	setPaymentsFailed(state: State, failed: boolean) {
		state.paymentsFailed = failed
	},
}

const actions: ActionTree<State, RootState> = {
	async getStripeCustomerPortal(
		{ commit, state },
		requestData,
	): Promise<StripeCustomerAttributes | void> {
		try {
			const stripePortalData: { data: StripeCustomerAttributes } = await axios.post(
				'/billing-portal',
				requestData,
			)

			let { data } = stripePortalData

			return data
		} catch (err: any) {
			if (err.handled) {
				return
			}
			RemoteLog.error({
				message: 'Stripe customer portal failed to open',
				context: errorSummary(err),
			})
		}
	},
	async getBillingData(
		{ commit, state },
		params = {} as RequestParams,
	): Promise<BillingsAttributes | void> {
		try {
			commit('setItemsLoading', true)

			const itemsData: { data: BillingsAttributes } = await axios.get('/billings', {
				params,
			})

			let { data } = itemsData

			commit('setBillingData', data)

			return data
		} catch (err: any) {
			if (err.handled) {
				return
			}
			RemoteLog.error({ message: 'Billing data failed to load', context: errorSummary(err) })
		} finally {
			commit('setItemsLoading', false)
		}
	},
	async getPaymentHistory({ commit }): Promise<void> {
		commit('setPaymentsLoading', true)
		commit('setPaymentsFailed', false)

		try {
			// The section renders its own failure, so Stripe being down must not toast over the rest of the page.
			const paymentsData: { data: PaymentHistoryAttributes } = await axios.get('/payment-history', {
				showsErrorsItself: true,
			} as AxiosRequestConfig)

			commit('setPayments', paymentsData.data.payments)
		} catch (err) {
			RemoteLog.error({ message: 'Payment history failed to load', context: errorSummary(err) })
			commit('setPaymentsFailed', true)
		} finally {
			commit('setPaymentsLoading', false)
		}
	},
}

const getters: GetterTree<State, RootState> = {}

const module: Module<State, RootState> = {
	namespaced: true,
	state,
	mutations,
	actions,
	getters,
}

export default module
